import React, { useState, useEffect } from 'react';
import './StreakCounter.css';

/**
 * StreakCounter - Creates habit formation through daily streaks
 * Psychological triggers: Loss aversion, achievement, habit loops
 */
export const StreakCounter = ({ 
  onStreakMilestone,
  onStreakBroken,
  showRecovery = true,
  compact = false,
  showMilestone: showMilestoneProp = true,
  className = ''
}) => {
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [lastVisit, setLastVisit] = useState(null);
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneReached, setMilestoneReached] = useState(null);
  const [streakBroken, setStreakBroken] = useState(false);
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  
  useEffect(() => {
    // Load streak data from localStorage
    const savedData = localStorage.getItem('streakData');
    if (savedData) {
      const data = JSON.parse(savedData);
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const lastVisitDate = new Date(data.lastVisit).toDateString();
      
      if (lastVisitDate === today) {
        // Already visited today
        setStreak(data.streak);
        setBestStreak(data.bestStreak);
        setLastVisit(data.lastVisit);
      } else if (lastVisitDate === yesterday) {
        // Continuing streak
        const newStreak = data.streak + 1;
        setStreak(newStreak);
        setBestStreak(Math.max(newStreak, data.bestStreak));
        setLastVisit(new Date().toISOString());
        
        // Check milestones
        checkMilestone(newStreak);
        
        // Save updated streak
        saveStreakData(newStreak, Math.max(newStreak, data.bestStreak));
      } else {
        // Streak broken
        setStreakBroken(true);
        setBestStreak(data.bestStreak);
        if (onStreakBroken) onStreakBroken(data.streak);
        
        // Start new streak
        setStreak(1);
        setLastVisit(new Date().toISOString());
        saveStreakData(1, data.bestStreak);
      }
    } else {
      // First visit
      setStreak(1);
      setBestStreak(1);
      setLastVisit(new Date().toISOString());
      saveStreakData(1, 1);
    }
  }, []);
  
  const saveStreakData = (currentStreak, best) => {
    localStorage.setItem('streakData', JSON.stringify({
      streak: currentStreak,
      bestStreak: best,
      lastVisit: new Date().toISOString()
    }));
  };
  
  const checkMilestone = (currentStreak) => {
    const milestones = [3, 7, 14, 21, 30, 50, 100];
    if (milestones.includes(currentStreak)) {
      setMilestoneReached(currentStreak);
      setShowMilestone(true);
      if (onStreakMilestone) onStreakMilestone(currentStreak);
      
      // Prompt to share at certain milestones
      if ([7, 30, 100].includes(currentStreak)) {
        setTimeout(() => setShowSharePrompt(true), 2000);
      }
      
      setTimeout(() => setShowMilestone(false), 5000);
    }
  };
  
  const getFireEmoji = () => {
    if (streak === 0) return '💤';
    if (streak < 3) return '🔥';
    if (streak < 7) return '🔥🔥';
    if (streak < 14) return '🔥🔥🔥';
    if (streak < 30) return '🔥💥🔥';
    return '🔥🌟🔥';
  };
  
  const getStreakMessage = () => {
    if (streak === 0) return 'Start your streak!';
    if (streak === 1) return 'Streak started!';
    if (streak < 3) return 'Keep it going!';
    if (streak < 7) return 'On fire!';
    if (streak < 14) return 'Unstoppable!';
    if (streak < 30) return 'Legendary!';
    return 'Quiz Master!';
  };
  
  const getMilestoneMessage = () => {
    const messages = {
      3: '3 Day Streak! 🎉 You\'re building a habit!',
      7: 'Week Warrior! 🏆 You\'re in the top 15%!',
      14: 'Two Weeks! 💪 Only 8% make it this far!',
      21: '21 Days! 🧠 Habit officially formed!',
      30: 'Month Master! 👑 You\'re in the elite 3%!',
      50: '50 Days! 🌟 Incredible dedication!',
      100: 'CENTURY! 💯 You\'re a legend!'
    };
    return messages[milestoneReached] || 'Milestone reached!';
  };
  
  const handleRecoverStreak = () => {
    // Offer to recover streak (could require watching ad or sharing)
    setStreakBroken(false);
    setShowSharePrompt(true);
  };
  
  const handleShare = () => {
    const text = `🔥 ${streak} day streak on PersonalityQuiz! Can you beat it?`;
    if (navigator.share) {
      navigator.share({
        title: 'My Quiz Streak',
        text: text,
        url: window.location.origin
      });
    } else {
      // Fallback to copy
      navigator.clipboard.writeText(text);
    }
    setShowSharePrompt(false);
  };
  
  const getDaysUntilNext = () => {
    const milestones = [3, 7, 14, 21, 30, 50, 100];
    const next = milestones.find(m => m > streak);
    return next ? next - streak : null;
  };
  
  const nextDays = getDaysUntilNext();
  
  // Compact mode for navigation bar
  if (compact) {
    return (
      <div className={`streak-counter ${className}`}>
        <div className={`streak-main ${streak > 0 ? 'active' : 'inactive'}`}>
          <span className={`fire-emoji ${streak > 7 ? 'intense' : ''}`}>
            {streak === 0 ? '💤' : '🔥'}
          </span>
          <span className="streak-number">
            <span className="number">{streak}</span>
            <span className="label"> day{streak !== 1 ? 's' : ''}</span>
          </span>
        </div>
      </div>
    );
  }
  
  // Full mode
  return (
    <div className={`streak-counter ${className}`}>
      <div className={`streak-main ${streak > 0 ? 'active' : 'inactive'}`}>
        <div className="streak-fire">
          <span className={`fire-emoji ${streak > 7 ? 'intense' : ''}`}>
            {getFireEmoji()}
          </span>
        </div>
        
        <div className="streak-info">
          <div className="streak-number">
            <span className="number">{streak}</span>
            <span className="label">day streak</span>
          </div>
          
          {!compact && (
            <div className="streak-message">
              {getStreakMessage()}
            </div>
          )}
          
          {!compact && nextDays && (
            <div className="next-milestone">
              {nextDays} day{nextDays > 1 ? 's' : ''} to next reward!
            </div>
          )}
        </div>
        
        {!compact && bestStreak > streak && (
          <div className="best-streak">
            Best: {bestStreak} days
          </div>
        )}
      </div>
      
      {/* Milestone celebration */}
      {showMilestone && (
        <div className="milestone-celebration">
          <div className="milestone-content">
            <span className="milestone-emoji">🎊</span>
            <div className="milestone-text">{getMilestoneMessage()}</div>
          </div>
        </div>
      )}
      
      {/* Streak broken warning */}
      {streakBroken && showRecovery && (
        <div className="streak-broken">
          <div className="broken-content">
            <span className="broken-emoji">😱</span>
            <div className="broken-text">
              You lost your {bestStreak} day streak!
            </div>
            <button 
              className="recover-button"
              onClick={handleRecoverStreak}
            >
              Recover Streak (Share to restore)
            </button>
          </div>
        </div>
      )}
      
      {/* Share prompt */}
      {showSharePrompt && (
        <div className="share-streak-prompt">
          <div className="share-content">
            <span className="share-emoji">📣</span>
            <div className="share-text">
              Share your {streak} day streak!
            </div>
            <div className="share-buttons">
              <button className="share-btn" onClick={handleShare}>
                Share Streak
              </button>
              <button 
                className="skip-btn" 
                onClick={() => setShowSharePrompt(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress bar to next milestone */}
      {nextDays && (
        <div className="milestone-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${((streak % 7) / 7) * 100}%` 
              }}
            />
          </div>
        </div>
      )}
      
      {/* Daily reminder */}
      {streak > 3 && (
        <div className="daily-reminder">
          <span className="reminder-icon">⏰</span>
          <span className="reminder-text">
            Come back tomorrow to keep your streak!
          </span>
        </div>
      )}
    </div>
  );
};

export default StreakCounter;